import { Injectable } from '@angular/core';
import { OrderStore } from '../stores/order.store';
import { OrderMenuStore } from '../stores/order-menu.store';
import { OrderMenuApi } from '../api/order-menu.api';
import { OrderMenuCartStore } from '../stores/order-menu-cart.store';
import { InvoiceStore } from '../stores/invoice.store';
import { ItemDetailsStore } from '../stores/item-details.store';
import { OrdersApi } from '../api/orders.api';
import { LocationStore } from '../stores/location.store';
import { BottomSheetService } from '../v2-components/services/bottom-sheet.service';
import {
  IOrderMenuCategory,
  IOrderMenuCategorySection,
  IOrderMenuItem,
  IOrderMenuItemGallery,
  IOrderMenuSchedule,
  IOrderMenuTag
} from '../pages/v2-order-pay/models/order-menu.models';
import { TimeSlotConflictSheet } from '../pages/v2-order-pay/bottom-sheets/time-slot-conflict-sheet/time-slot-conflict.sheet';
import {
  IMenuV2Badge,
  IMenuV2Category,
  IMenuV2ItemGallery,
  IMenuV2Section,
  IMenuV2Tag,
  MenuV2EntityStatus,
  MenuV2ItemChoice,
  MenuV2ItemsArray
} from '../api/contracts/menu-v2.contracts';
import { OrderAheadStore } from '../stores/order-ahead.store';
import { v4 as uuid } from 'uuid';
import { ItemGroupSheet } from '../pages/v2-order-pay/bottom-sheets/item-group-sheet/item-group.sheet';
import { RouterService } from 'services/router.service';
import { ReadonlyItemDetailsSheet } from '../pages/v2-order-pay/bottom-sheets/readonly-item-details-sheet/readonly-item-details.sheet';
import { RxUtil } from 'utility/rx.util';
import { differenceInHours, format, parseISO } from 'date-fns';
import { hasKey } from 'utility/unknown.util';
import { ReadonlyItemDetailsStore } from 'stores/readonly-item-details-store.store';
import { ErrorService } from './error.service';

@Injectable({
  providedIn: 'root'
})
export class OrderMenuService {
  constructor(
    private orderMenuStore: OrderMenuStore,
    private orderMenuApi: OrderMenuApi,
    private orderMenuCartStore: OrderMenuCartStore,
    private invoiceStore: InvoiceStore,
    private itemDetailsStore: ItemDetailsStore,
    private readonlyItemDetailsStore: ReadonlyItemDetailsStore,
    private ordersApi: OrdersApi,
    private locationStore: LocationStore,
    private bottomSheetService: BottomSheetService,
    private orderAheadStore: OrderAheadStore,
    private routerService: RouterService,
    private errorService: ErrorService
  ) {}

  async loadOrderMenu(): Promise<void> {
    let categories: IOrderMenuCategory[];
    const menu = await this.orderMenuApi.getMenuV2();
    categories = this.mapMenuV2CategoriesToUICategories(menu.menus);
    this.orderMenuStore.itemGalleries =
      this.mapMenuV2ItemGalleriesToUIItemGalleries(menu.itemGalleries ?? []);
    this.orderMenuCartStore.setMenuPopularItems(
      this.recursivelyMapMenuV2ItemsToUIItems(menu.popularItems)
    );

    // Expand sections based on default setting
    categories.forEach((category) => {
      const shouldCategoryBeExpanded = category.sections.every(
        (section) => section.defaultExpansionBehavior === 'expanded'
      );
      if (shouldCategoryBeExpanded) {
        this.orderMenuStore.toggleExpandCategory(category);
      } else {
        category.sections.forEach((section) => {
          if (section.defaultExpansionBehavior === 'expanded') {
            this.orderMenuStore.toggleExpandSection(section);
          }
        });
      }
    });

    this.orderMenuStore.orderMenuCategories = categories;
  }

  private mapMenuV2CategoriesToUICategories(
    menuV2Categories: IMenuV2Category[]
  ): IOrderMenuCategory[] {
    return menuV2Categories.map<IOrderMenuCategory>((category, i) => ({
      id: category._id,
      name: category.displayName,
      description: category.description ?? '',
      isDisabled: category.status === MenuV2EntityStatus.Disabled,
      sections: category.sections.map<IOrderMenuCategorySection>((section) => ({
        id: section.uid,
        title: section.displayName,
        description: section.description ?? '',
        isDisabled: section.status === MenuV2EntityStatus.Disabled,
        items: this.recursivelyMapMenuV2ItemsToUIItems(section.items),
        isExpanded: false,
        nestedItemGalleries: section.itemGallery
          ? this.mapMenuV2ItemGalleriesToUIItemGalleries([section.itemGallery])
          : [],
        defaultExpansionBehavior: section.defaultAppearance ?? 'expanded',
        schedule: this.parseScheduleTimes(section)
      })),
      isExpanded: false,
      sortOrder: category.sortOrder ?? i,
      entityType: 'category',
      nestedItemGalleries: category.itemGallery
        ? this.mapMenuV2ItemGalleriesToUIItemGalleries([category.itemGallery])
        : [],
      itemGalleriesBefore: [],
      itemGalleriesAfter: [],
      schedule: this.parseScheduleTimes(category)
    }));
  }

  private parseScheduleTimes(
    v: IMenuV2Category | IMenuV2Section
  ): IOrderMenuSchedule | null {
    return v.scheduleTimes?.start && v.scheduleTimes.end
      ? {
          startTime: parseISO(v.scheduleTimes.start),
          endTime: parseISO(v.scheduleTimes.end)
        }
      : null;
  }

  private mapTagContractsToUIModel(contracts?: IMenuV2Tag[]): IOrderMenuTag[] {
    return (contracts || []).map<IOrderMenuTag>((c: IMenuV2Tag) => {
      return {
        abbreviated: c.shortCode ?? '',
        full: c.name ?? ''
      };
    });
  }

  private mapBadgeContractToUIModel(contract?: IMenuV2Badge) {
    return {
      text: contract?.text ?? '',
      backgroundColor: contract?.backgroundColor ?? '',
      textColor: contract?.textColor ?? ''
    };
  }

  private recursivelyMapMenuV2ItemsToUIItems(
    menuV2Items: MenuV2ItemsArray
  ): IOrderMenuItem[] {
    const self = this;

    return menuV2Items.map<IOrderMenuItem>(function recursiveParseItem(
      item: MenuV2ItemChoice
    ): IOrderMenuItem {
      const itemId = item.itemId ?? item._id;
      if (!itemId) {
        throw Error('Menu v2 item has no id field set');
      }

      return {
        id: itemId,
        type: item.sectionItemType === 'item' ? 'item' : 'itemGroup',
        price: 'price' in item ? item.price ?? 0 : 0,
        name: item.displayName,
        description: item.description ?? '',
        hasModifiers:
          Array.isArray(item.modifierGroups) && item.modifierGroups.length > 0,
        inStock: 'inStock' in item ? item.inStock : true,
        image: {
          url: item.imageUrl ?? '',
          width: item.imageWidth ?? 0,
          height: item.imageHeight ?? 0,
          thumbnailUrl: item.thumbnailImageUrl ?? '',
          thumbnailWidth: item.thumbnailImageWidth ?? 0,
          thumbnailHeight: item.thumbnailImageHeight ?? 0
        },
        attributes:
          'alcohol' in item
            ? {
                alcohol: !!item.alcohol?.containsAlcohol,
                alcoholUnits: item.alcohol?.quantity?.value ?? 0
              }
            : {
                alcohol: false,
                alcoholUnits: 0
              },
        recommendedItems: ('recommendedItems' in item
          ? item.recommendedItems
          : []
        ).map(recursiveParseItem),
        items: ('items' in item ? item.items : []).map(recursiveParseItem),
        isDisabled: false,
        isUnavailableDueToAlcohol: false,
        badge: self.mapBadgeContractToUIModel(item.badge),
        tags: self.mapTagContractsToUIModel(item.tags),
        isPinned: false
      };
    });
  }

  private mapMenuV2ItemGalleriesToUIItemGalleries(
    menuV2ItemGalleries: IMenuV2ItemGallery[]
  ): IOrderMenuItemGallery[] {
    return menuV2ItemGalleries.map((itemGallery, i) => ({
      id: uuid(),
      items: this.recursivelyMapMenuV2ItemsToUIItems(itemGallery.items),
      title: itemGallery.displayName ?? '',
      description: itemGallery.description ?? '',
      sortOrder: itemGallery.sortOrder ?? i,
      entityType: 'item-gallery'
    }));
  }

  async onItemClick(item: IOrderMenuItem): Promise<void> {
    if (item.type === 'itemGroup') {
      this.orderMenuStore.selectedItemGroup = item;
      this.bottomSheetService.showScrollableBottomSheetVariationHeader(
        ItemGroupSheet,
        item.name,
        item.description
      );
    } else {
      const isMenuReadonly = await RxUtil.takeOne(
        this.locationStore.isMenuReadonly$
      );

      if (isMenuReadonly) {
        this.readonlyItemDetailsStore.clearStack();
        this.viewReadonlyItemDetails(item);
      } else {
        await this.routerService.viewItemDetailsPage(item);
      }
    }
  }

  viewReadonlyItemDetails(item: IOrderMenuItem) {
    this.itemDetailsStore.selectedItemId = item.id;
    this.bottomSheetService.showBottomSheet(ReadonlyItemDetailsSheet);
  }

  async saveItemToCart(): Promise<void> {
    if (!this.itemDetailsStore.selectedItemId)
      throw 'No selected item to add to cart';

    this.orderMenuCartStore.saveItemToCart(
      await RxUtil.takeOne(this.itemDetailsStore.selectedItem$),
      await RxUtil.takeOne(
        this.itemDetailsStore.modifierGroupsForSelectedItem$
      ),
      this.itemDetailsStore.selectedItemDetails
    );
  }

  async submitOrder(): Promise<void> {
    if (
      this.locationStore.currentLocation?.orderDoesNotRequireImmediatePayment
    ) {
      return;
    }

    try {
      this.invoiceStore.setCurrentInvoice(
        await this.orderMenuApi.submitOrder()
      );
    } catch (e) {
      const error: Error | {} = hasKey(e, 'error') ? (e.error as Error) : {};

      if (
        hasKey(error, 'code') &&
        hasKey(error, 'data') &&
        error.code === 'TIME_SLOT_FULL'
      ) {
        return this.handleTimeSlotConflictError(
          // @ts-ignore
          error.data,
          this.submitOrder.bind(this)
        );
      } else if (hasKey(error, 'message')) {
        this.errorService.performSubmitOrderErrorHandling(
          (error as Error).message
        );
      } else {
        throw e;
      }
    }
  }

  private async handleTimeSlotConflictError(
    errorData: {
      closestAvailableFutureTime?: string;
      closestAvailablePastTime?: string;
    },
    boundSubmitOrderFunction: () => Promise<void>
  ): Promise<void> {
    this.orderAheadStore.setTimeSlotConflictSuggestions(
      errorData.closestAvailablePastTime ?? null,
      errorData.closestAvailableFutureTime ?? null
    );

    const bottomSheetRef = this.bottomSheetService.showBottomSheet<string>(
      TimeSlotConflictSheet,
      !!(
        errorData.closestAvailablePastTime ||
        errorData.closestAvailableFutureTime
      )
    );

    const selectedIsoTime = await RxUtil.takeOne(bottomSheetRef.onClose$);

    if (selectedIsoTime) {
      this.orderAheadStore.setSelectedTime(selectedIsoTime);
      return boundSubmitOrderFunction();
    } else {
      throw new Error('Please select a new time to complete your order.');
    }
  }

  async loadOrderAheadTimes(): Promise<void> {
    this.orderAheadStore.setOrderAheadTimes(
      await this.ordersApi.getOrderAheadTimes(
        this.locationStore.currentLocation?._id ?? ''
      )
    );
  }

  getItemGalleryImageUrl(item: IOrderMenuItem): string {
    return item.image.url || 'assets/images/ReadyImageEmptyState.png';
  }

  shouldShowItemGalleryImages(itemGallery: IOrderMenuItemGallery): boolean {
    return itemGallery.items.some((item) => item.image.url);
  }

  private formatScheduleTime(date: Date): string {
    return format(date, 'h:mmaaa');
  }

  // Note: we are not currently handling the stale data issue
  getScheduleText(schedule: IOrderMenuSchedule): string {
    return this.isScheduleLessThanOneHourFromExpiry(schedule, new Date())
      ? `Available today until ${this.formatScheduleTime(schedule.endTime)}`
      : `Available today ${this.formatScheduleTime(
          schedule.startTime
        )} - ${this.formatScheduleTime(schedule.endTime)}`;
  }

  isScheduleLessThanOneHourFromExpiry(
    schedule: IOrderMenuSchedule,
    now: Date
  ): boolean {
    return differenceInHours(schedule.endTime, now) < 1;
  }
}
